import React from 'react';
import { colors, spacing, typography, shadows } from '../tokens';

/**
 * MetricCard — KPI summary tile.
 *
 * Props:
 *   title    — string metric label
 *   value    — string | number formatted value to display
 *   change   — string optional trend badge text (e.g. "+12%")
 *   trend    — 'up' | 'down' (controls badge color)
 *   icon     — ReactNode icon element
 *   iconBg   — string CSS color for the icon container background.
 *              When provided, the icon is rendered inside a rounded square
 *              with this background color. Omit for a plain icon.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function MetricCard(_ref) {
  var title = _ref.title,
    value = _ref.value,
    change = _ref.change,
    _ref$trend = _ref.trend,
    trend = _ref$trend === void 0 ? 'up' : _ref$trend,
    icon = _ref.icon,
    iconBg = _ref.iconBg;
  var isPositive = trend === 'up';
  var cardStyle = {
    fontFamily: typography.fontFamily,
    backgroundColor: 'var(--bafkal-bg-surface)',
    borderRadius: '8px',
    border: "1px solid ".concat(colors.neutral[200]),
    boxShadow: shadows.sm,
    padding: spacing.lg
  };
  var badgeStyle = {
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: "2px ".concat(spacing.sm),
    borderRadius: '12px',
    backgroundColor: isPositive ? colors.success[50] : colors.danger[50],
    color: isPositive ? colors.success[700] : colors.danger[700]
  };
  var iconContainerStyle = iconBg ? {
    backgroundColor: iconBg,
    borderRadius: '8px',
    padding: spacing.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  } : null;
  return /*#__PURE__*/_jsxs("div", {
    style: cardStyle,
    children: [/*#__PURE__*/_jsxs("div", {
      style: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: spacing.sm
      },
      children: [/*#__PURE__*/_jsx("span", {
        style: {
          fontSize: typography.sizes.sm,
          color: colors.neutral[500]
        },
        children: title
      }), icon && (iconContainerStyle ? /*#__PURE__*/_jsx("div", {
        style: iconContainerStyle,
        children: icon
      }) : icon)]
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        display: 'flex',
        alignItems: 'baseline',
        gap: spacing.sm
      },
      children: [/*#__PURE__*/_jsx("span", {
        style: {
          fontSize: typography.sizes['2xl'],
          fontWeight: 700,
          color: colors.neutral[900]
        },
        children: value
      }), change && /*#__PURE__*/_jsx("span", {
        style: badgeStyle,
        children: change
      })]
    })]
  });
}

/**
 * ChartContainer — white card wrapper for chart.js / recharts elements.
 *
 * Props:
 *   title    — string optional section heading
 *   children — the chart component
 */
export function ChartContainer(_ref2) {
  var title = _ref2.title,
    children = _ref2.children;
  return /*#__PURE__*/_jsxs("div", {
    style: {
      backgroundColor: 'var(--bafkal-bg-surface)',
      borderRadius: '8px',
      border: "1px solid ".concat(colors.neutral[200]),
      boxShadow: shadows.sm,
      padding: spacing.lg,
      fontFamily: typography.fontFamily
    },
    children: [title && /*#__PURE__*/_jsx("h4", {
      style: {
        margin: "0 0 ".concat(spacing.md, " 0"),
        fontSize: typography.sizes.lg,
        fontWeight: 600,
        color: colors.neutral[900]
      },
      children: title
    }), /*#__PURE__*/_jsx("div", {
      style: {
        width: '100%',
        minHeight: '300px'
      },
      children: children
    })]
  });
}